import React, { useEffect, useRef } from 'react';
import { getStyle, getWinScale } from '@utils/util';
import { MobileHeaderHeight, ScreenWidth } from '@constants/value';
import Loading from '@components/loading';
import { useRefStateSync } from '@utils/hooks';

import './index.less';

interface IProps {
    children: React.ReactNode[];
    renderIndicator?: (index: number) => React.ReactNode | string;
}

const ScrollContainer: React.FC<IProps> = ({ children, renderIndicator }) => {
    const itemOffsetArr = useRef<number[]>();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollTrackRef = useRef<HTMLDivElement>(null);
    const scrollBarRef = useRef<HTMLDivElement>(null);
    const startInfo = useRef<any>({});
    const trackWidth = useRef<number>();
    const [curIndex, curIndexRef, setCurIndex] = useRefStateSync<number>(0);
    const bodyScrollTop = useRef<number>(0);

    const wrapper = wrapperRef.current;
    const scrollBar = scrollBarRef.current;
    const scrollTrack = scrollTrackRef.current;

    useEffect(() => {
        if (!wrapper || wrapper.children.length <= 0) {
            return;
        }

        init();
        addScrollListener();

        return () => {
            removeScrollListener();
        };
    }, [children]);

    const init = () => {
        setTimeout(() => {
            initWrapper();
            initScrollBar();
        });
    };

    const initWrapper = () => {
        const wrapper = wrapperRef.current;
        if (!wrapper || !wrapper.children) {
            return;
        }
        let width = 0;
        const offsetArr: number[] = [];
        Array.from(wrapper.children).forEach((item, index) => {
            const marginLeft = Number(getStyle(item, 'marginLeft').replace('px', ''));
            const marginRight = Number(getStyle(item, 'marginRight').replace('px', ''));
            const scale = getWinScale();
            width = width + item?.clientWidth + marginLeft + marginRight;
            let offsetLeft;
            if (index === 0) {
                // @ts-ignore
                offsetLeft = -item.offsetLeft + marginLeft;
            } else {
                // @ts-ignore
                offsetLeft = -item.offsetLeft + (ScreenWidth * scale - item.clientWidth) / 2;
            }
            offsetArr.push(offsetLeft);
        });
        itemOffsetArr.current = offsetArr;
        bodyScrollTop.current = -wrapper.getBoundingClientRect().y;
        wrapper.style.width = `${width}px`;
    };

    const initScrollBar = () => {
        if (!scrollTrack || !scrollBar || !wrapper) {
            return;
        }
        scrollBar.style.width = `${scrollTrack.clientWidth / wrapper.children.length}px`;
        trackWidth.current = scrollTrack.clientWidth - scrollBar.clientWidth + 1;
    };

    const moveStartFn = e => {
        const scrollBarX = scrollBar?.getBoundingClientRect().x || 0;
        const scrollTrackX = scrollTrack?.getBoundingClientRect().x || 0;
        startInfo.current = {
            startX: e.touches[0].pageX,
            startY: e.touches[0].pageY,
            startOffset: wrapper?.getBoundingClientRect().x,
            scrollbarStartOffset: scrollBarX - scrollTrackX,
        };
    };

    const moveEndFn = e => {
        const endX = e.changedTouches[0].pageX;
        const { keepY, startX, startOffset, scrollbarStartOffset } = startInfo.current;
        const offsetArr = itemOffsetArr.current || [];
        const scale = getWinScale();
        const i = curIndexRef.current;
        if (keepY) {
            delete startInfo.current.keepY;
            return;
        }
        if (Math.abs(endX - startX) < 50 * scale) {
            // 如果滚动小于100px，不加减
            animationSlide(wrapper, startOffset);
            animationSlide(scrollBar, scrollbarStartOffset);
        } else if (endX - startX < 0) {
            // 往右滚动加一屏
            if (i + 1 <= offsetArr.length - 1) {
                setCurIndex(i + 1);
                animationSlide(wrapper, offsetArr[curIndexRef.current]);
                animationSlide(scrollBar, getScrollBarTrans(offsetArr[curIndexRef.current]));
                scrollToTop();
                return;
            }
            animationSlide(wrapper, offsetArr[offsetArr.length - 1]);
        } else {
            // 往左滚动减一屏
            if (i - 1 >= 0) {
                setCurIndex(i - 1);
                animationSlide(wrapper, offsetArr[curIndexRef.current]);
                animationSlide(scrollBar, getScrollBarTrans(offsetArr[curIndexRef.current]));
                scrollToTop();
                return;
            }
            animationSlide(wrapper, offsetArr[0]);
        }
    };

    const scrollToTop = () => {
        // 滚回顶部
        if (!wrapper) {
            return;
        }
        const originTop = wrapper.offsetTop;
        const curTop = originTop - wrapper.getBoundingClientRect().y;
        const headerHight = MobileHeaderHeight * getWinScale();
        const targetTop = originTop - headerHight;
        if (curTop > targetTop) {
            window.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
    };

    const movingFn = e => {
        const curY = e.touches[0].pageY;
        const curX = e.touches[0].pageX;
        const { startX, startY, keepY } = startInfo.current;
        const moveY = Math.abs(curY - startY);
        const moveX = Math.abs(curX - startX);
        let isScrollY = keepY;

        // 如果还未设置
        if (isScrollY === undefined) {
            if (moveY > moveX) {
                startInfo.current = {
                    ...startInfo.current,
                    keepY: true, // 直到touchend结束前都保持只滚动Y轴
                };
            } else {
                startInfo.current = {
                    ...startInfo.current,
                    keepY: false, // 直到touchend结束前都保持只滚动X轴
                };
            }
        }

        isScrollY = startInfo.current.keepY;

        if (!isScrollY && wrapper) {
            // 阻止原生touch事件
            e.preventDefault();
            const { startOffset } = startInfo.current;
            const translateX = curX - startX + startOffset;
            const offsetArr = itemOffsetArr.current || [];
            wrapper.style.transform = `translateX(${translateX}px)`;
            if (translateX > offsetArr[0] || translateX <= offsetArr[offsetArr.length - 1]) {
                return;
            }
            scrollBar && (scrollBar.style.transform = `translateX(${getScrollBarTrans(translateX)}px)`);
        }
    };

    const addScrollListener = () => {
        wrapper?.addEventListener('touchstart', moveStartFn);
        wrapper?.addEventListener('touchend', moveEndFn);
        wrapper?.addEventListener('touchmove', movingFn);
        window.addEventListener('resize', init);
    };

    const removeScrollListener = () => {
        wrapper?.removeEventListener('touchstart', moveStartFn);
        wrapper?.removeEventListener('touchend', moveEndFn);
        wrapper?.removeEventListener('touchmove', movingFn);
        window.removeEventListener('resize', init);
    };

    const getScrollBarTrans = (wrapperX: number) => {
        const trackW = trackWidth.current || 0;
        const offsetArr = itemOffsetArr.current || [];
        return (wrapperX / offsetArr[offsetArr.length - 1]) * trackW;
    };

    const animationSlide = (item, translateX = 0) => {
        if (!wrapper || !item) {
            return;
        }
        item.style.transition = 'transform .3s';
        item.style.transform = `translateX(${Math.round(translateX)}px)`;
        setTimeout(() => {
            item.style.transition = 'none';
        }, 300);
    };

    return (
        <div className="scroll-container">
            {children.length > 0 ? (
                <>
                    <div className="scroll-wrapper" ref={wrapperRef}>
                        {children.map((item, index) => (
                            <div className="scroll-item" key={index}>
                                {item}
                            </div>
                        ))}
                    </div>
                    {renderIndicator ? (
                        renderIndicator(curIndex)
                    ) : (
                        <div className="scroll-bar" ref={scrollTrackRef}>
                            <span ref={scrollBarRef} />
                        </div>
                    )}
                </>
            ) : (
                <Loading />
            )}
        </div>
    );
};

export default ScrollContainer;
